/* prettier-ignore */

import { CommonModule } from '@angular/common';
import { Component, inject, input, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth/services';
import { Snackbar } from 'src/app/services';

/**
 * HRS-3134: salida para quien se registró en una oficina de turismo y no puede entrar.
 *
 * Al registrarse, el técnico le crea la cuenta sin contraseña —igual que el alta con
 * Google—, así que si intenta acceder solo ve un «credenciales incorrectas» y se
 * queda pensando que no tiene cuenta. Esto aparece tras un intento fallido y le manda
 * el enlace de acceso de siempre (el de recuperar contraseña, que a una cuenta sin
 * contraseña le sirve igual).
 *
 * No se le dice si su correo está registrado o no: el aviso es el mismo en los dos
 * casos, como la respuesta del back.
 */
@Component({
  selector: 'app-office-access-link',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './office-access-link.component.html',
  styleUrls: ['./office-access-link.component.scss'],
})
export class OfficeAccessLinkComponent {
  private _authService = inject(AuthService);
  private _snackbar = inject(Snackbar);

  /** Correo escrito en el formulario de acceso. */
  public email = input<string | null>(null);

  /** Dónde se pinta el aviso: el diálogo de Material lo necesita en el body. */
  public snackbarContainer = input<HTMLElement | undefined>(undefined);

  protected sending = signal<boolean>(false);
  protected sent = signal<boolean>(false);

  protected send(): void {
    if (this.sending() || this.sent()) {
      return;
    }

    const email = (this.email() ?? '').trim();

    // Sin correo no hay a dónde mandarlo, y el back lo rechazaría con un 422 que el
    // visitante no sabría interpretar.
    if (!email) {
      this._notify('Escribe tu correo y te enviamos el enlace de acceso');
      return;
    }

    this.sending.set(true);

    this._authService.sendAccessLink(email).subscribe({
      // El mismo aviso exista o no la cuenta: es lo que responde el back.
      next: () => this._done(),
      error: () => this._done(),
    });
  }

  private _done(): void {
    this.sending.set(false);
    this.sent.set(true);
    this._notify('Te hemos enviado un enlace de acceso a tu correo');
  }

  /** El snackbar ya traduce lo que se le pasa. */
  private _notify(message: string): void {
    this._snackbar.showSuccessSnackbar(message, undefined, this.snackbarContainer());
  }
}
